import { html, raw } from '../lib/html.js';
import { isValidEmail, onlyDigits, passwordProblems } from '../lib/masks.js';
import { autoClearErrors, busy, confirm, setFieldError, toastError, toastSuccess, valueOf } from '../lib/ui.js';
import { google } from '../services/google.js';
import { flow } from '../lib/flow.js';
import { auth } from '../services/auth.js';
import { Page, brand, go } from './base.js';

/** Esqueci a senha: 1) e-mail → código; 2) código + nova senha. */
class ForgotPage extends Page {
  template() {
    return html`
      <ion-header class="ion-no-border">
        <ion-toolbar>
          <ion-buttons slot="start"><ion-back-button default-href="/login" text="Voltar"></ion-back-button></ion-buttons>
        </ion-toolbar>
      </ion-header>
      <ion-content class="ion-padding auth">
        <div class="auth-wrap">
          ${raw(brand('Recuperar senha'))}

          <form class="step-email" novalidate>
            <p class="hint">Informe seu e-mail. Se houver uma conta, enviaremos um código.</p>
            <ion-list lines="none" class="form">
              <ion-input name="email" type="email" label="E-mail" label-placement="floating" fill="outline"
                autocomplete="email" inputmode="email" autocapitalize="off"></ion-input>
            </ion-list>
            <ion-button type="submit" expand="block" class="submit-email">Enviar código</ion-button>
          </form>

          <form class="step-reset" novalidate hidden>
            <p class="hint">Digite o código enviado para <strong class="email"></strong> e a nova senha.</p>
            <ion-list lines="none" class="form">
              <ion-input name="code" label="Código" label-placement="floating" fill="outline" inputmode="numeric"
                autocomplete="one-time-code" maxlength="6" class="code-input"></ion-input>
              <ion-input name="password" type="password" label="Nova senha" label-placement="floating" fill="outline"
                autocomplete="new-password" helper-text="Mínimo 8 caracteres, com maiúscula, minúscula e número">
                <ion-input-password-toggle slot="end"></ion-input-password-toggle>
              </ion-input>
            </ion-list>
            <ion-button type="submit" expand="block" class="submit-reset">Salvar nova senha</ion-button>
          </form>
        </div>
      </ion-content>`;
  }

  setup() {
    autoClearErrors(this);
    this.$('.step-email').addEventListener('submit', (e) => {
      e.preventDefault();
      this.sendCode();
    });
    this.$('.step-reset').addEventListener('submit', (e) => {
      e.preventDefault();
      this.reset();
    });
  }

  onEnter() {
    this.$('.step-email').hidden = false;
    this.$('.step-reset').hidden = true;
    if (flow.email) this.$('ion-input[name="email"]').value = flow.email;
  }

  async sendCode() {
    const email = valueOf(this, 'email').toLowerCase();
    if (!isValidEmail(email)) return setFieldError(this.$('ion-input[name="email"]'), 'Informe um e-mail válido');

    await busy(this.$('.submit-email'), async () => {
      try {
        await auth.forgotPassword(email);
        this.email = email;
        this.$('.email').textContent = email;
        this.$('.step-email').hidden = true;
        this.$('.step-reset').hidden = false;
      } catch (err) {
        if (err.code === 'GOOGLE_ACCOUNT') return this.googleAccount();
        if (err.code === 'APPLE_ACCOUNT') return this.appleAccount();
        toastError(err.message);
      }
    });
  }

  /** Conta criada pelo Google não tem senha: oferece entrar pelo Google. */
  async googleAccount() {
    const ok = await confirm({
      header: 'Esta conta entra com Google',
      message: 'Ela foi criada pelo Google e não tem senha no TicketCar. Entre com a sua conta Google.',
      confirmText: 'Entrar com Google',
      cancelText: 'Voltar',
    });
    if (ok) await google.start();
  }

  /** Conta criada pela Apple não tem senha: oferece entrar pela Apple. */
  async appleAccount() {
    const ok = await confirm({
      header: 'Esta conta entra com Apple',
      message: 'Ela foi criada pela Apple e não tem senha no TicketCar. Entre com a sua conta Apple.',
      confirmText: 'Continuar com a Apple',
      cancelText: 'Voltar',
    });
    if (ok) await google.start('apple');
  }

  async reset() {
    const code = onlyDigits(valueOf(this, 'code'));
    const password = this.$('ion-input[name="password"]').value ?? '';
    let ok = true;
    if (code.length !== 6) (ok = false), setFieldError(this.$('ion-input[name="code"]'), 'Digite os 6 dígitos');
    const problems = passwordProblems(password);
    if (problems.length) (ok = false), setFieldError(this.$('ion-input[name="password"]'), `Falta: ${problems.join(', ')}`);
    if (!ok) return;

    await busy(this.$('.submit-reset'), async () => {
      try {
        await auth.confirmForgotPassword(this.email, code, password);
        flow.email = this.email;
        toastSuccess('Senha alterada. Entre com a nova senha.');
        go('/login', 'root');
      } catch (err) {
        if (err.code === 'INVALID_CODE' || err.code === 'CODE_EXPIRED') setFieldError(this.$('ion-input[name="code"]'), err.message);
        else if (err.code === 'WEAK_PASSWORD') setFieldError(this.$('ion-input[name="password"]'), err.message);
        else toastError(err.message);
      }
    });
  }
}

customElements.define('page-forgot', ForgotPage);
